import { EMPTY_OBJECT, EMPTY_STRING } from '../constants/common'
import { REG_EXP_EMAIL } from '../constants/regExp'
import { PROFILE_ROUTE_NAMES } from '../constants/route'
import {
  ADMIN_USER_ROLE,
  CONTRACTOR_USER_ROLE,
  DASHBOARD_USER_ROLES,
  DWELLER_USER_ROLE,
  DWELLER_USER_ROLES,
  EXTERNAL_CONTACT_USER_ROLE,
  LANDLORD_USER_ROLE,
  MANAGER_USER_ROLE,
  ROLE_TRANSLATION_ALIASES,
  STAFF_USER_ROLES,
  TEAM_VISIBILITY_MODE_HIDDEN,
  USER_SELECT_OLD_API_DEFAULT_AVATAR_SEARCH_STRING,
  USER_STATUS_ACTIVE,
  USER_STATUS_BLOCKED,
  USER_STATUS_CODE_ACTIVE,
  USER_STATUS_CODE_BLOCKED,
  USER_STATUS_CODE_INVITED,
  USER_STATUS_CODE_NOT_CONFIRMED,
  USER_STATUS_EXISTED,
  USER_STATUS_IMPORTED,
  USER_STATUS_INVITED,
  USER_STATUS_NOT_CONFIRMED,
  USER_STATUS_UNEXISTED,
  WORKER_USER_ROLE,
} from '../constants/user'
import i18n from '../i18n'

import { isValueObjectType, isValueStringType } from './common'

import UserIcon from '../icons/user.svg'

function isAutoCreatedUser(email, fullname) {
  return email?.split('@')[0] === fullname
}

// `avatar` is either a bare URL string (lists/tags) or `{ id, url }` (detail
// profile endpoint); both helpers accept either shape.
export function getUserAvatarUrl(avatar) {
  return isValueStringType(avatar) ? avatar : (avatar?.url ?? null)
}

export function getUserAvatarDocumentId(avatar) {
  return isValueObjectType(avatar) ? (avatar?.id ?? null) : null
}

export function isDefaultOldApiAvatar(avatar) {
  const url = getUserAvatarUrl(avatar)

  return (
    Boolean(url) &&
    url.includes(USER_SELECT_OLD_API_DEFAULT_AVATAR_SEARCH_STRING)
  )
}

export function getUserAvatar(user) {
  const avatarUrl = getUserAvatarUrl(user.avatar)

  return {
    Icon: user.is_system ? UserIcon : null,
    avatarDocumentId: getUserAvatarDocumentId(user.avatar),
    color: user.is_system ? null : user.avatar_color,
    src: isDefaultOldApiAvatar(user.avatar) ? null : avatarUrl,
    text: user.fullname,
  }
}

export function getUserListItemContentAdditionalText({
  companyPostName,
  group,
  isLandlord,
  role,
}) {
  if (companyPostName || role || group) {
    return ` | ${companyPostName || i18n.t(ROLE_TRANSLATION_ALIASES[isLandlord ? LANDLORD_USER_ROLE : role || group])}`
  }

  return EMPTY_STRING
}

export function getUserId(user) {
  return user?.id || user?.owner
}

export function isStaffUser({ role }) {
  return STAFF_USER_ROLES.includes(role)
}

export function hasDashboardAccess({ role }) {
  return DASHBOARD_USER_ROLES.includes(role)
}

export function isDwellerRole({ role }) {
  return DWELLER_USER_ROLES.includes(role)
}

export function isTeamUserProfileHidden({ currentUser, isTeamHidden, user }) {
  return (
    isTeamHidden &&
    !isStaffUser(currentUser) &&
    isStaffUser({ role: user?.role || user?.group })
  )
}

export function isTeamVisibilityModeHidden(mode) {
  return mode === TEAM_VISIBILITY_MODE_HIDDEN
}

export function isManagerUser({ role }) {
  return role === MANAGER_USER_ROLE
}

export function isAdminUser({ role }) {
  return isAdminUserRole(role)
}

export function isAdminUserRole(role) {
  return role === ADMIN_USER_ROLE
}

export function isWorkerUser({ role }) {
  return role === WORKER_USER_ROLE
}

export function isExternalContactUser({ role }) {
  return role === EXTERNAL_CONTACT_USER_ROLE
}

export function isContractorUser({ role }) {
  return role === CONTRACTOR_USER_ROLE
}

export function isLandlordUser({ role }) {
  return role === LANDLORD_USER_ROLE
}

export function isUnconfirmed(user) {
  if (!user) {
    return false
  }

  return user.role === DWELLER_USER_ROLE && user.validated === false
}

export function getUserFullNameFromEmail(email) {
  return email.split('@', 1)[0]
}

export function getUserFullName(user) {
  const { fullname, name, second_name: secondName } = user ?? EMPTY_OBJECT

  return fullname || [name, secondName].filter(Boolean).join(' ')
}

export function getUserListItemContentName(email, fullname) {
  return isAutoCreatedUser(email, fullname) || (!fullname && email)
    ? email
    : fullname
}

export function getUserListItemContentText({
  companyPostName,
  email,
  fullname,
  group,
  isLandlord,
  role,
}) {
  return (
    getUserListItemContentName(email, fullname) +
    getUserListItemContentAdditionalText({
      companyPostName,
      group,
      isLandlord,
      role,
    })
  )
}

export function getUserContactInfo({ email, phone }) {
  if (email && phone) {
    return `${email}  •  +${phone}`
  }

  if (!email && !phone) {
    return EMPTY_STRING
  }

  return email || `+${phone}`
}

export function isUserStatusActive(status) {
  return status === USER_STATUS_ACTIVE
}

export function isUserStatusBlocked(status) {
  return status === USER_STATUS_BLOCKED
}

export function isUserStatusExisted(status) {
  return status === USER_STATUS_EXISTED
}

export function isUserStatusImported(status) {
  return status === USER_STATUS_IMPORTED
}

export function isUserStatusCodeActive(status) {
  return status === USER_STATUS_CODE_ACTIVE
}

export function isUserStatusCodeBlocked(status) {
  return status === USER_STATUS_CODE_BLOCKED
}

export function isUserStatusCodeInvited(status) {
  return status === USER_STATUS_CODE_INVITED
}

export function isUserStatusCodeNotConfirmed(status) {
  return status === USER_STATUS_CODE_NOT_CONFIRMED
}

export function isUserStatusInvited(status) {
  return status === USER_STATUS_INVITED
}

export function isUserStatusNotConfirmed(status) {
  return status === USER_STATUS_NOT_CONFIRMED
}

export function isUserStatusUnexisted(status) {
  return status === USER_STATUS_UNEXISTED
}

export function getUserProfileRoute({
  id,
  isExternalContactProfileNGAvailable = false,
  isProviderProfileNGAvailable = false,
  isUserProfileNGAvailable,
  role,
}) {
  switch (true) {
    case isExternalContactUser({ role }):
      return isExternalContactProfileNGAvailable
        ? PROFILE_ROUTE_NAMES.externalContactProfileNG({ id })
        : PROFILE_ROUTE_NAMES.externalContacts({ id })

    case isContractorUser({ role }):
      return isProviderProfileNGAvailable
        ? PROFILE_ROUTE_NAMES.providerProfileNG({ id })
        : PROFILE_ROUTE_NAMES.provider({ id })

    case (isDwellerRole({ role }) || isStaffUser({ role })) &&
      isUserProfileNGAvailable:
      return PROFILE_ROUTE_NAMES.userProfileNG({ id })

    default:
      return PROFILE_ROUTE_NAMES.inhabitant({ id })
  }
}

export function isUserFullNameEmail(fullName) {
  return REG_EXP_EMAIL.test(fullName)
}
